/**
 * The tracking wrapper. The only module the rest of the app imports.
 *
 * A component names a funnel step and passes parameters; the descriptor table
 * decides which of GA4, Google Ads and Meta hear about it. Components never
 * name a vendor, so adding a destination to a step is a one-line change here
 * rather than a new call in every component.
 *
 * Three guarantees live in trackEvent rather than being repeated per helper:
 *   - page_location on every GA4 event (this is how conversion-by-page works)
 *   - a try/catch per destination, so a Meta failure cannot cost us the GA4
 *     event and nothing can ever reach the UI. A tracking throw once made a
 *     succeeded booking show the failure screen; that must stay impossible.
 *   - a development log of the resolved fan-out, not just the event name
 */

import Cookie from 'js-cookie';

import { COOKIE_CONSENT } from '../../constants/cookies';
import {
  CURRENCY, EVENTS, FUNNEL_SOURCE_UNKNOWN, QUEUE_GOOGLE_EVENTS,
} from '../../constants/analytics';
import { getCookieConsent } from '../cookies';

import { DEPRECATED_BOOKING_COMPLETED, EVENT_DESCRIPTORS } from './events';
import * as gtag from './gtag';
import * as meta from './meta';
import * as queue from './queue';
import { buildPurchase, purchaseContents, purchaseParams } from './params';


const isDev = process.env.NODE_ENV === 'development';

const devLog = (...args) => {
  if (isDev) console.log('[tracking]', ...args); // eslint-disable-line no-console
};

export const CONSENT_UNDECIDED = 'undecided';
export const CONSENT_GRANTED = 'granted';
export const CONSENT_DECLINED = 'declined';

/**
 * Marketing consent as three states. getCookieConsent collapses a missing
 * cookie and a declined one into the same false, which is right for deciding
 * whether to fire but wrong for deciding whether to hold.
 */
export function getMarketingConsentState() {
  if (typeof window === 'undefined') return CONSENT_UNDECIDED;
  if (typeof Cookie.get(COOKIE_CONSENT) !== 'string') return CONSENT_UNDECIDED;
  return getCookieConsent().marketing ? CONSENT_GRANTED : CONSENT_DECLINED;
}

// Meta's field names for the parameters we already have. Overridable per call
// for the events where Meta wants something structurally different.
function defaultMetaParams(params) {
  const metaParams = {};
  if (params.room_type) {
    metaParams.content_type = 'product';
    metaParams.content_ids = [params.room_type];
  } else if (params.service_code) {
    // An add-on is a catalogue entry too, under its service code, so the
    // AddToCart it raises joins the same product funnel as the room and the
    // Purchase that lists both.
    metaParams.content_type = 'product';
    metaParams.content_ids = [params.service_code];
    metaParams.contents = [{ id: params.service_code, quantity: params.quantity || 1 }];
  }
  if (params.value !== undefined) {
    metaParams.value = params.value;
    metaParams.currency = params.currency || CURRENCY;
  }
  if (params.check_in_date && params.check_out_date) {
    metaParams.search_string = `${params.check_in_date} to ${params.check_out_date}`;
  }
  return metaParams;
}

/* The consent cookie is shared between tabs; what each vendor was told is not.
   A tab that was open while the guest decided in another tab still runs gtag
   under the denied defaults and, for Meta, has no pixel at all. Before every
   event the cookie is compared with what this tab last applied, and a change
   is applied here too: Google gets the consent update (so its events stop
   going out as denied), a revoke reaches the pixel and drops what was held.
   The Meta grant itself is handled just below, where the pixel is loaded. */
let appliedConsent = null;

const consentKey = (consent) => `${consent.analytics ? 'a' : '-'}${consent.marketing ? 'm' : '-'}`;

function syncConsentFromCookie() {
  if (typeof window === 'undefined' || typeof Cookie.get(COOKIE_CONSENT) !== 'string') return;
  const consent = getCookieConsent();
  const key = consentKey(consent);
  if (appliedConsent === key) return;
  appliedConsent = key;

  try {
    gtag.updateGoogleConsent(consent);
  } catch (e) {
    // Never reaches the UI.
  }
  try {
    // Both directions: a revoke reaches a loaded pixel and drops what was
    // held; a grant after a revoke reaches the same pixel again, which the
    // load-on-demand path below cannot do because the pixel is already there.
    const fresh = meta.updateMetaConsent(consent);
    if (consent.marketing) {
      if (fresh) meta.track('PageView');
      queue.flush();
    } else {
      queue.clear();
    }
  } catch (e) {
    // Never reaches the UI.
  }
}

/* Loads the pixel in this tab when the consent cookie says granted but nothing
   here has run the grant yet. That is the tab that was open when the guest
   accepted in another one: the cookie is shared, the pixel is not, and
   meta.track() on a missing pixel is a silent no-op. Anything held here while
   the tab was undecided replays first, in order. */
function ensurePixelForGrantedConsent(metaEventName) {
  if (meta.isLoaded()) return;
  const fresh = meta.updateMetaConsent({ marketing: true });
  if (fresh && metaEventName !== 'PageView') meta.track('PageView');
  const flushed = queue.flush();
  if (flushed) devLog(`pixel loaded late in this tab, flushed ${flushed} held Meta events`);
}

function sendMeta(metaEventName, metaParams, options, queueable) {
  const consent = getMarketingConsentState();
  const fire = () => meta.track(metaEventName, metaParams, options);

  if (consent === CONSENT_GRANTED) {
    ensurePixelForGrantedConsent(metaEventName);
    fire();
    return 'sent';
  }
  if (consent === CONSENT_UNDECIDED && queueable) {
    return queue.enqueue(fire) ? 'queued' : 'dropped(full)';
  }
  return 'dropped';
}

/**
 * The fan-out. One canonical step becomes up to three vendor calls.
 */
export function trackEvent(eventKey, params = {}, metaOverrides = undefined, metaOptions = {}) {
  const descriptor = EVENT_DESCRIPTORS[eventKey];
  if (!descriptor) {
    devLog('unknown event key', eventKey);
    return;
  }

  /* source last, not first. Spreading params over a default means an explicit
     `source: undefined` (which trackPageView and trackRefund both pass) wins
     and the default is lost, so the highest-volume event on the site went out
     with no funnel source at all. */
  const withSource = { ...params, source: params.source || FUNNEL_SOURCE_UNKNOWN };
  let adsResult = '-';
  let metaResult = '-';

  syncConsentFromCookie();

  try {
    gtag.pushEvent(descriptor.ga4, withSource, { alsoAds: Boolean(descriptor.adsTag) });
  } catch (e) {
    // Never reaches the UI.
  }

  if (descriptor.ads) {
    try {
      const conversionParams = {};
      if (withSource.value !== undefined) {
        conversionParams.value = withSource.value;
        conversionParams.currency = withSource.currency || CURRENCY;
      }
      if (withSource.transaction_id) conversionParams.transaction_id = withSource.transaction_id;
      adsResult = gtag.pushConversion(descriptor.ads, conversionParams) ? descriptor.ads : 'skipped(no label)';
    } catch (e) {
      adsResult = 'error';
    }
  }

  if (descriptor.meta) {
    try {
      const metaParams = { ...defaultMetaParams(withSource), ...(metaOverrides || {}) };
      metaResult = sendMeta(descriptor.meta, metaParams, metaOptions, descriptor.queueable);
    } catch (e) {
      metaResult = 'error';
    }
  }

  devLog(`${eventKey} -> ga4:${descriptor.ga4 || '-'} ads:${adsResult} meta:${descriptor.meta || '-'}(${metaResult})`, withSource);
}

/* react-helmet swaps document.title after the route change, so reading it in
   onRouteUpdate catches the previous page's title. One frame of delay fixes it
   and lets the Meta PageView drop its own bespoke 500ms timer.

   The location is captured now, not at fire time: a redirect inside the delay
   (BookingRoute sending a guest with no stay from /book/details to the rooms
   page) would otherwise report the destination twice and the origin never.
   The pending timer is also replaced, so that redirect yields exactly one
   page_view, for the page the guest actually ends up on. */
let pageViewTimer = null;

export function trackPageView(source) {
  if (typeof window === 'undefined') return;
  const pageLocation = window.location.href;
  if (pageViewTimer) clearTimeout(pageViewTimer);
  pageViewTimer = setTimeout(() => {
    pageViewTimer = null;
    trackEvent(EVENTS.PAGE_VIEW, {
      source,
      page_location: pageLocation,
      page_title: document.title,
      page_referrer: document.referrer,
    });
  }, 32);
}

export function trackScrollDepth(source) {
  trackEvent(EVENTS.SCROLL_DEPTH, { source });
}

/**
 * Same signature as the helper it replaces: the saga call sites and their
 * existing effect assertions only change their import path.
 *
 * This is the single seam every handled failure in the booking sagas passes
 * through, so it reports to Sentry as well as GA. GA alone was not enough: the
 * sagas catch every failure and the SDK only captures what goes unhandled, so
 * a guest who could not complete a booking produced no Sentry signal at all.
 * GA was also the wrong place to look, because it is gated behind analytics
 * consent, so failures from guests who declined cookies were recorded nowhere.
 *
 * `fatal` already separates "the booking is broken" from "the guest hit a limit
 * and can retry", so it picks the Sentry level and alert rules can key on it.
 * window.Sentry is what gatsby-plugin-sentry exposes; guarded because it is
 * absent without a DSN, under jest, and before the SDK chunk loads. No PII is
 * passed: callers send an error string, never a booking payload.
 */
export function trackException(description, fatal) {
  try {
    gtag.pushException(description, fatal);
  } catch (e) {
    // Never reaches the UI.
  }

  try {
    if (typeof window !== 'undefined' && window.Sentry && typeof window.Sentry.captureMessage === 'function') {
      window.Sentry.captureMessage(description, {
        level: fatal ? 'error' : 'warning',
        extra: { fatal: !!fatal },
      });
    }
  } catch (e) {
    // Never reaches the UI.
  }
}

/**
 * Everything the Conversions API needs to match and deduplicate this purchase,
 * captured in the browser before the booking request goes out. The same
 * event_id is sent to Meta twice: once from here, once from the function that
 * proxies the booking. Meta collapses the pair.
 */
export function createPurchaseContext() {
  return {
    eventId: `mh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
  };
}

export function trackPurchase({ reservation, services, source, eventId }) {
  const purchase = buildPurchase(reservation, services, eventId);
  const params = { ...purchaseParams(purchase), source: source || FUNNEL_SOURCE_UNKNOWN };

  trackEvent(EVENTS.PURCHASE, params, purchaseContents(purchase), purchase.eventId ? { eventID: purchase.eventId } : {});

  /* Deprecated. purchase above is the conversion and carries the same
     transaction; this stays only so anything already reporting on the old name
     keeps working. Remove once GA4 and Ads confirm nothing references it. */
  try {
    gtag.pushEvent(DEPRECATED_BOOKING_COMPLETED, {
      source: params.source,
      transaction_id: purchase.transactionId,
      /* The display name, not the code, because that is what this event has
         always sent. Keeping booking_completed alive is pointless if the one
         dimension a report is most likely to group by silently changes meaning
         underneath it. `purchase` carries the code in room_type; this event
         deliberately does not. */
      room_type: purchase.roomName,
      room_name: purchase.roomName,
      check_in_date: purchase.checkInDate,
      check_out_date: purchase.checkOutDate,
      value: purchase.value,
      currency: purchase.currency,
      nights: purchase.nights,
    });
  } catch (e) {
    // Never reaches the UI.
  }

  return purchase;
}

export function trackRefund({ transactionId, value, currency, source }) {
  trackEvent(EVENTS.REFUND, {
    source,
    transaction_id: transactionId,
    value,
    currency: currency || CURRENCY,
  });
}

/**
 * Applies the stored consent to both vendors. Called once on load and again
 * every time the guest writes a consent decision.
 */
export function updateConsent() {
  const consent = getCookieConsent();
  appliedConsent = consentKey(consent);

  try {
    gtag.updateGoogleConsent(consent);
  } catch (e) {
    // Never reaches the UI.
  }

  try {
    const fresh = meta.updateMetaConsent(consent);
    // Order matters: the pixel must have consent before the held events replay.
    if (consent.marketing) {
      // The page consent was given on: page views are not held (see events.js),
      // so this is its only PageView, and it is what Meta's snippet sends on load.
      if (fresh) meta.track('PageView');
      const flushed = queue.flush();
      if (flushed) devLog(`flushed ${flushed} held Meta events`);
    } else {
      queue.clear();
    }
  } catch (e) {
    // Never reaches the UI.
  }
}

export function initAnalytics() {
  const consent = getCookieConsent();
  // Only a decided cookie counts as applied; undecided stays open so the first
  // event after a decision elsewhere picks it up.
  appliedConsent = typeof window !== 'undefined' && typeof Cookie.get(COOKIE_CONSENT) === 'string'
    ? consentKey(consent) : null;

  try {
    gtag.updateGoogleConsent(consent);
  } catch (e) {
    // Never reaches the UI.
  }

  try {
    meta.initMeta(consent);
  } catch (e) {
    // Never reaches the UI.
  }

  if (QUEUE_GOOGLE_EVENTS) devLog('Google event queueing is enabled');
}
